/**
 * Shared Organization Profile Strength calculator.
 *
 * Single source of truth for Organization Profile %:
 * - Client Profile → Profile Strength sidebar
 * - EA Personalize → Organization Profile card / journey widget
 *
 * Do not substitute /api/.../readiness or /strength scores for this metric.
 */

const DVA_COLORS = {
  navy: '#18365E',
  orange: '#FF6E4C',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
};

/**
 * @param {Record<string, any> | null | undefined} profile
 * @returns {{
 *   score: number,
 *   label: string,
 *   color: string,
 *   breakdown: { critical: number, optional: number, quality: number },
 *   missingFields: string[],
 * }}
 */
export function calculateProfileStrength(profile) {
  if (!profile) {
    return {
      score: 0,
      label: 'Not Started',
      color: DVA_COLORS.error,
      breakdown: { critical: 0, optional: 0, quality: 0 },
      missingFields: ['Organization Name', 'Organization Type', 'Total Beds'],
    };
  }

  // Critical fields (60% weight)
  const criticalFields = [
    { key: 'client_name', alt: 'organization_name', label: 'Organization Name' },
    { key: 'organization_type', label: 'Organization Type' },
    { key: 'beds', alt: 'total_beds', label: 'Total Beds', requirePositive: true },
    { key: 'lives_under_vbc_agreements', alt: 'lives_under_vbc', label: 'Lives Under VBC' },
    {
      key: 'market_competition_level',
      label: 'Market Competition Level',
      validate: (v) => ['High', 'Moderate', 'Low', 'high', 'moderate', 'low'].includes(v),
    },
    { key: 'annual_revenue', label: 'Annual Revenue', requirePositive: true },
    { key: 'ehr_vendor', label: 'EHR Vendor' },
  ];

  // Optional fields (30% weight)
  const optionalFields = [
    { key: 'employed_providers', label: 'Employed Providers' },
    { key: 'affiliates', label: 'Affiliates' },
    { key: 'attributed_lives', label: 'Attributed Lives' },
    { key: 'operating_margin_pct', label: 'Operating Margin' },
    { key: 'rural_percentage', alt: 'rural_pct', label: 'Rural %' },
    { key: 'urban_percentage', alt: 'urban_pct', label: 'Urban %' },
    { key: 'suburban_percentage', alt: 'suburban_pct', label: 'Suburban %' },
    { key: 'payer_mix_medicare_pct', label: 'Medicare %' },
    { key: 'payer_mix_medicaid_pct', label: 'Medicaid %' },
    { key: 'payer_mix_commercial_pct', label: 'Commercial %' },
  ];

  const isFilled = (value, requirePositive = false, validate = null) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (requirePositive && (isNaN(Number(value)) || Number(value) <= 0)) return false;
    if (validate && !validate(value)) return false;
    return true;
  };

  const getValue = (field) => {
    let value = profile[field.key];
    if (!isFilled(value) && field.alt) {
      value = profile[field.alt];
    }
    return value;
  };

  let criticalFilled = 0;
  const missingFields = [];

  for (const field of criticalFields) {
    const value = getValue(field);
    if (isFilled(value, field.requirePositive, field.validate)) {
      criticalFilled++;
    } else {
      missingFields.push(field.label);
    }
  }
  const criticalScore = Math.round((criticalFilled / criticalFields.length) * 100);

  let optionalFilled = 0;
  for (const field of optionalFields) {
    const value = getValue(field);
    if (isFilled(value)) {
      optionalFilled++;
    }
  }
  const optionalScore = Math.round((optionalFilled / optionalFields.length) * 100);

  let qualityPassed = 0;
  const qualityTotal = 4;

  const beds = Number(profile.beds || profile.total_beds);
  if (isNaN(beds) || beds === 0 || (beds >= 10 && beds <= 10000)) {
    qualityPassed++;
  }

  const vbcLives = Number(profile.lives_under_vbc_agreements || profile.lives_under_vbc);
  if (isNaN(vbcLives) || (vbcLives >= 0 && vbcLives <= 10000000)) {
    qualityPassed++;
  }

  const rural = Number(profile.rural_percentage || profile.rural_pct) || 0;
  const urban = Number(profile.urban_percentage || profile.urban_pct) || 0;
  const suburban = Number(profile.suburban_percentage || profile.suburban_pct) || 0;
  const geoTotal = rural + urban + suburban;
  if (geoTotal === 0 || (geoTotal >= 95 && geoTotal <= 105)) {
    qualityPassed++;
  }

  const medicare = Number(profile.payer_mix_medicare_pct) || 0;
  const medicaid = Number(profile.payer_mix_medicaid_pct) || 0;
  const commercial = Number(profile.payer_mix_commercial_pct) || 0;
  const selfPay = Number(profile.self_pay_percentage) || 0;
  const payerTotal = medicare + medicaid + commercial + selfPay;
  if (payerTotal === 0 || (payerTotal >= 95 && payerTotal <= 105)) {
    qualityPassed++;
  }

  const qualityScore = Math.round((qualityPassed / qualityTotal) * 100);

  const total = Math.round(
    criticalScore * 0.6 + optionalScore * 0.3 + qualityScore * 0.1
  );

  let label = 'Needs Work';
  let color = DVA_COLORS.error;
  if (total >= 90) {
    label = 'Excellent';
    color = DVA_COLORS.success;
  } else if (total >= 75) {
    label = 'Good';
    color = DVA_COLORS.success;
  } else if (total >= 50) {
    label = 'Fair';
    color = DVA_COLORS.warning;
  } else if (total >= 25) {
    label = 'Needs Attention';
    color = DVA_COLORS.orange;
  }

  return {
    score: total,
    label,
    color,
    breakdown: {
      critical: criticalScore,
      optional: optionalScore,
      quality: qualityScore,
    },
    missingFields,
  };
}

export default calculateProfileStrength;
